import { CreateJobResponse } from '@parkchirp/reporting';
import ky from 'ky';
import { useCallback, useMemo, useState } from 'react';
import { CreateReport, CreateReportRequest } from './types';

type UseCreateReportArgs = {
	baseUrl: string;
	authToken: string;
};

type UseCreateReportResult = {
	clearCreatedJob: () => void;
	createReport: CreateReport;
	createdJob: CreateJobResponse | null;
	isCreatingReport: boolean;
	error: string | null;
};

const getErrorMessage = (error: unknown) => {
	if (error instanceof Error) {
		return error.message;
	}

	return 'Failed to create report.';
};

export const useCreateReport = ({ baseUrl, authToken }: UseCreateReportArgs): UseCreateReportResult => {
	const [createdJob, setCreatedJob] = useState<CreateJobResponse | null>(null);
	const [isCreatingReport, setIsCreatingReport] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const api = useMemo(() => {
		return ky.create({
			prefixUrl: baseUrl,
			timeout: 30000,
			retry: { limit: 0 },
			headers: {
				Authorization: `Bearer ${authToken}`,
			},
		});
	}, [authToken, baseUrl]);

	const clearCreatedJob = useCallback(() => {
		setCreatedJob(null);
	}, []);

	const createReport = useCallback(
		async (request: CreateReportRequest) => {
			setCreatedJob(null);
			setIsCreatingReport(true);
			setError(null);

			try {
				const job = await api.post('jobs', { json: request }).json<CreateJobResponse>();
				setCreatedJob(job);
			} catch (error) {
				setError(getErrorMessage(error));
			} finally {
				setIsCreatingReport(false);
			}
		},
		[api],
	);

	return {
		clearCreatedJob,
		createReport,
		createdJob,
		isCreatingReport,
		error,
	};
};
